/**
 * useAppleLoginSync — Ensures Apple IAP subscriptions are synced on every login.
 *
 * Problem: If a user purchases via Apple IAP (especially before account creation),
 * the subscription may not get synced to the backend. This hook runs on every
 * SIGNED_IN event on iOS and proactively checks RevenueCat for active entitlements,
 * then syncs them to the backend if no record exists.
 *
 * This is a safety net — it catches cases where:
 * 1. The post-purchase sync failed silently
 * 2. The RevenueCat webhook couldn't resolve the user
 * 3. The user purchased anonymously and the identity transfer didn't complete
 *
 * IMPORTANT: Keep the auth state listener synchronous — async work must be fired
 * outside the callback so auth restoration cannot get blocked on native startup.
 */

import { useEffect, useRef } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isIOSNative } from "@/services/revenuecat";

async function syncAppleLoginSubscription(session: Session) {
  try {
    console.log("[AppleLoginSync] SIGNED_IN on iOS — checking Apple subscription sync...");

    // 1. Login to RevenueCat with the real user ID (ensures identity is linked)
    const { initRevenueCat, loginRevenueCat, hasActiveEntitlement, getSubscriptionStatus } =
      await import("@/services/revenuecat");

    await initRevenueCat();
    await loginRevenueCat(session.user.id, {
      email: session.user.email ?? null,
      displayName:
        (session.user.user_metadata?.name as string | undefined) ??
        (session.user.user_metadata?.full_name as string | undefined) ??
        null,
    });
    console.log("[AppleLoginSync] RevenueCat identity set to:", session.user.id);

    // 2. Check if there's an active entitlement
    const active = await hasActiveEntitlement();
    if (!active) {
      console.log("[AppleLoginSync] No active Apple entitlement — skipping sync");
      return;
    }

    // 3. Check if backend already has a record
    const { data: backendData } = await supabase.functions.invoke("get-my-subscription");
    if (backendData?.access_allowed) {
      console.log("[AppleLoginSync] Backend already grants access — no sync needed");
      return;
    }

    // 4. Sync the Apple subscription to the backend
    console.log("[AppleLoginSync] Active entitlement found but no backend access — syncing...");
    const status = await getSubscriptionStatus();
    if (status.productId) {
      const { error } = await supabase.functions.invoke("sync-apple-subscription", {
        body: {
          product_id: status.productId,
          is_active: status.isActive,
          expiration_date: status.expirationDate,
          is_sandbox: status.isSandbox,
          will_renew: status.willRenew,
          period_type: status.periodType,
        },
      });
      if (error) {
        console.error("[AppleLoginSync] Sync failed:", error);
      } else {
        console.log("[AppleLoginSync] ✅ Apple subscription synced to backend successfully");
      }
    }
  } catch (err) {
    console.error("[AppleLoginSync] Error during login sync:", err);
  }
}

export function useAppleLoginSync() {
  const hasSynced = useRef(false);

  useEffect(() => {
    // Only relevant on iOS native
    if (!isIOSNative()) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" || !session?.user?.id) return;
      if (hasSynced.current) return;
      hasSynced.current = true;

      void syncAppleLoginSubscription(session);
    });

    return () => {
      subscription.unsubscribe();
      // Reset on unmount so re-mount can sync again
      hasSynced.current = false;
    };
  }, []);
}
